import { zodResolver } from '@hookform/resolvers/zod';
import { ExtendedAddress } from '@porterin/booking-flow/src/order-review/domain/entities/ExtendedAddress';
import {
  handleAddressDetailsGeocode,
  retryAddressDetailsGeocode,
} from '@porterin/booking-flow/src/order-review/view/helpers/GeoCodeHelper';
import { useHook } from '@porterin/booking-flow/src/order-review/view/hooks/GoogleAutoCompleteHook';
import {
  FormData,
  schema,
  AddressType,
} from '@porterin/booking-flow/src/order-review/view/types/OrderReviewAddressSchema';
import { useLocation } from '@porterin/component-library/src/location-accuracy/contexts/LocationContext';
import { getGoogleMapsHookConfig } from '@porterin/component-library/src/location-accuracy/utils/LocationAccuracyHelpers';
import { createAddressChangeHandler } from '@porterin/component-library/src/location-accuracy/utils/AddressChangeHandler';
import { useForm } from 'react-hook-form';

const createPredictionClickHandler =
  (
    onAddress: (address: ExtendedAddress) => void,
    resetPredictions: () => void,
  ) =>
  (prediction: google.maps.places.AutocompletePrediction): void => {
    handleAddressDetailsGeocode(
      prediction.place_id,
      (address: ExtendedAddress) => onAddress(address),
      (_error: any) => {
        retryAddressDetailsGeocode(
          prediction.place_id,
          (address: ExtendedAddress) => onAddress(address),
          prediction.description,
        );
      },
      prediction.description,
    );
    resetPredictions();
  };

export const useSelectLocationController = () => {
  const {
    register,
    formState: { errors },
  } = useForm<FormData>({
    mode: 'onBlur',
    resolver: zodResolver(schema),
  });

  const { selectLocation, state } = useLocation();
  const { getPredictions, predictions, resetPredictions } = useHook(
    getGoogleMapsHookConfig(),
  );

  const handleAddressChange = createAddressChangeHandler(
    getPredictions,
    resetPredictions,
  );

  const handlePredictionClick = createPredictionClickHandler(
    (address: ExtendedAddress) => selectLocation(address),
    resetPredictions,
  );
  const updateField = register(
    state.waypoint === 'Pickup' ? AddressType.pickup : AddressType.drop,
    { onChange: handleAddressChange },
  );

  return {
    predictions,
    errors,
    updateField,
    handlePredictionClick,
  };
};

export type UseSelectLocationControllerReturn = ReturnType<
  typeof useSelectLocationController
>;
